import { FIELD_TYPE_IDS } from "../../constants";
import { OperatorDescriptor, TDropdownNullableOption } from "../../models";
import { parseNumberComparisonValue } from "../table.filters.helpers";

const BETWEEN_BOUNDS_COUNT = 2;

/**
 * Filter field types whose stored values encode an OPERATOR choice, not a plain value list.
 * These are sent to the backend as `criteria.where` operator descriptors (RYDE-8067) instead of the
 * legacy `criteria.filters[key] = { value }` shape, which has no operator slot.
 */
export const isOperatorReportFilterType = (
  filterFieldTypeId: number | undefined,
): boolean =>
  filterFieldTypeId === FIELD_TYPE_IDS.FILTER.number_comparison ||
  filterFieldTypeId === FIELD_TYPE_IDS.FILTER.number_between;

/**
 * Decode a stored report filter value into a `criteria.where` operator descriptor.
 *
 * - number_comparison stores one `"op:value"` string (same encoding as the grid) → `{ $gt: value }` etc.
 * - number_between stores `[min, max]` → `{ $between: [min, max] }`; anything short of two finite
 *   bounds is an incomplete filter.
 *
 * Returns undefined for anything unreadable so an incomplete filter is skipped rather than sent —
 * the backend rejects malformed descriptors with 422, and a half-typed filter is not an error.
 */
export const buildReportWhereDescriptor = ({
  filterFieldTypeId,
  values,
}: {
  filterFieldTypeId: number | undefined;
  values: unknown;
}): OperatorDescriptor | undefined => {
  if (!Array.isArray(values) || !values.length) {
    return undefined;
  }

  if (filterFieldTypeId === FIELD_TYPE_IDS.FILTER.number_comparison) {
    // The cast only narrows the checked unknown[]; parseNumberComparisonValue typeof-guards each
    // element itself and returns null for anything that is not a readable "op:value" string.
    const parsed = parseNumberComparisonValue(
      values as TDropdownNullableOption[],
    );
    return parsed ? { [parsed.criteria]: parsed.operand } : undefined;
  }

  if (filterFieldTypeId === FIELD_TYPE_IDS.FILTER.number_between) {
    if (values.length !== BETWEEN_BOUNDS_COUNT) {
      return undefined;
    }
    const bounds = values.map(Number);
    return bounds.every(Number.isFinite) ? { $between: bounds } : undefined;
  }

  return undefined;
};
